<template>
    <e-dropdown ref="filtersDropdown" v-bind="$attrs" aria-role="list">
        <template #trigger="{ active }">
            <e-button
                type="is-primary"
                :class="{ 'is-outlined': appliedFiltersCount === 0 }"
                class="tw:w-full"
                :icon-right="active ? 'angle-up' : 'angle-down'"
                @click.prevent
            >
                <span v-if="appliedFiltersCount === 0">{{ translate('filters') }}</span>
                <span v-else>{{ translate('filters') }} ({{ appliedFiltersCount }})</span>
            </e-button>
        </template>
        <e-dropdown-item custom :focusable="false" class="filter-dropdown-item">
            <div class="filter-accordion">
                <div v-for="filter in filters" :key="filter.field" class="filter-section">
                    <div
                        :class="{ 'is-active': expandedSections[filter.field] }"
                        role="button"
                        :aria-expanded="expandedSections[filter.field] ? 'true' : 'false'"
                        :aria-controls="`filter-content-${filter.field}`"
                        tabindex="0"
                        @click.stop="handleSectionToggle(filter)"
                        @keydown.enter="handleSectionToggle(filter)"
                        @keydown.space.prevent="handleSectionToggle(filter)"
                    >
                        <div class="tw:flex tw:items-center">
                            <span class="filter-name-text">{{ filter.label }}</span>
                            <span v-if="isFilterApplied(filter)" class="tag is-primary is-small filter-active-tag">
                                {{ translate('active') }}
                            </span>
                            <div style="margin-left: auto">
                                <e-icon
                                    :icon="expandedSections[filter.field] ? 'angle-up' : 'angle-down'"
                                    size="is-small"
                                    class="filter-arrow-icon"
                                />
                            </div>
                        </div>
                    </div>

                    <transition name="slide-fade" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
                        <div
                            v-show="expandedSections[filter.field]"
                            :id="`filter-content-${filter.field}`"
                            class="filter-content tw:w-full"
                            role="region"
                            :aria-labelledby="`filter-header-${filter.field}`"
                        >
                            <RoleFilterItem
                                v-if="filter.type === 'roles'"
                                :filter="filter"
                                :available-roles="availableRoles"
                                :local-selected-role-ids="filterData.localSelectedRoleIds"
                                :add-role-to-filter="addRoleToFilter"
                                :remove-role-from-filter="removeRoleFromFilter"
                                :get-role-name="getRoleName"
                            />

                            <StatusFilterItem
                                v-else-if="filter.type === 'statuses'"
                                :filter="filter"
                                :available-statuses="availableStatuses"
                                :local-selected-status-names="filterData.localSelectedStatusNames"
                                :add-status-to-filter="addStatusToFilter"
                                :remove-status-from-filter="removeStatusFromFilter"
                            />

                            <TextFilterItem
                                v-else-if="filter.type === 'text'"
                                :filter="filter"
                                :model-value="formFields[filter.field] || ''"
                                placeholder="Enter text..."
                                :apply-filter="debouncedApplyFilter"
                            />

                            <TextFilterItem
                                v-else-if="filter.type === 'quiz'"
                                :filter="filter"
                                :model-value="formFields[filter.field] || ''"
                                placeholder="Enter quiz name..."
                                :apply-filter="debouncedApplyFilter"
                            />

                            <div v-else-if="filter.type === 'groups'" class="unified-filter-row" @click.stop>
                                <GroupSearch
                                    :model-value="localManuallyAppliedGroup"
                                    :set-value="localManuallyAppliedGroup?.name"
                                    class="tw:w-full"
                                    @update:modelValue="event => handleGroupFilter(event, filter)"
                                />
                            </div>

                            <GroupFilterItem
                                v-else-if="filter.type === 'multi-groups'"
                                :filter="filter"
                                :available-groups="availableGroups"
                                :group-load-error="filterData.groupLoadError"
                                :local-selected-group-ids="filterData.localSelectedGroupIds"
                                :add-group-to-filter="addGroupToFilter"
                                :remove-group-from-filter="removeGroupFromFilter"
                                :get-group-name="getGroupName"
                            />

                            <ActivityTypeFilterItem
                                v-else-if="filter.type === 'activity_type'"
                                :filter="filter"
                                :model-value="filter.value"
                                :apply-filter="debouncedApplyFilter"
                            />

                            <AutocompleteFilterItem
                                v-else-if="isAutocompleteFilter(filter.type)"
                                :filter="filter"
                                :filter-type="filter.type"
                                :apply-filter="debouncedApplyFilter"
                                :apply-filter-immediate="applyFilterImmediate"
                                :course="course"
                                :tag-type="tagType"
                                :local-manually-applied-group="localManuallyAppliedGroup"
                                :persist-tag-selected-title="selectedTitle => persistTagSelectedTitle(filter, selectedTitle)"
                            />

                            <EitherOrFilterItem
                                v-else-if="filter.type === 'either-or'"
                                :filter="filter"
                                :apply-filter="handleEitherOrFilter"
                            />

                            <MinMaxFilterItem
                                v-else-if="filter.type === 'min-max'"
                                :filter="filter"
                                :model-value="formFields[filter.field] || { min: '', max: '' }"
                                :apply-filter="handleMinMaxFilter"
                            />

                            <DateRangeFilterItem
                                v-else-if="filter.type === 'date-range'"
                                :filter="filter"
                                :model-value="getDateModelValue(filter.field)"
                                :apply-filter="handleDateRangeFilter"
                                :apply-filter-immediate="applyFilterImmediate"
                                :expanded-sections="expandedSections"
                                @close-filter-dropdown="closeDropdown"
                                @keep-filter-dropdown-open="keepDropdownOpen"
                            />

                            <DropdownFilterItem
                                v-else-if="filter.type === 'dropdown'"
                                :filter="filter"
                                :model-value="filter.value"
                                :options="filter.options"
                                :apply-filter="handleDropdownFilter"
                                @update:modelValue="value => updateFilterValue(filter, value)"
                            />
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Clear Filters Button -->
            <div class="unified-filter-row clear-filters-row" style="border-top: 1px solid var(--border)">
                <button class="button is-small clear-filters-button" @click="clearAllFilters">
                    <e-icon icon="times-circle" size="is-small" class="mr-2" />
                    <span class="filter-name-text">{{ translate('clear_filters') }}</span>
                </button>
            </div>
        </e-dropdown-item>
    </e-dropdown>
</template>

<script>
import { getCurrentInstance, nextTick, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import GroupSearch from '@/components/autocomplete/GroupSearch.vue';
import { shouldCloseFilterDropdownAfterApply, useFilterManager } from '@/composables/useFilterManager.js';
import Course from '@/models/Course.js';

// Import all filter item components
import ActivityTypeFilterItem from './FilterDropdown/items/ActivityTypeFilterItem.vue';
import AutocompleteFilterItem from './FilterDropdown/items/AutocompleteFilterItem.vue';
import DateRangeFilterItem from './FilterDropdown/items/DateRangeFilterItem.vue';
import DropdownFilterItem from './FilterDropdown/items/DropdownFilterItem.vue';
import EitherOrFilterItem from './FilterDropdown/items/EitherOrFilterItem.vue';
import GroupFilterItem from './FilterDropdown/items/GroupFilterItem.vue';
import MinMaxFilterItem from './FilterDropdown/items/MinMaxFilterItem.vue';
import RoleFilterItem from './FilterDropdown/items/RoleFilterItem.vue';
import StatusFilterItem from './FilterDropdown/items/StatusFilterItem.vue';
import TextFilterItem from './FilterDropdown/items/TextFilterItem.vue';

export default {
    name: 'FilterDropdown',

    components: {
        RoleFilterItem,
        StatusFilterItem,
        TextFilterItem,
        MinMaxFilterItem,
        DateRangeFilterItem,
        EitherOrFilterItem,
        DropdownFilterItem,
        AutocompleteFilterItem,
        ActivityTypeFilterItem,
        GroupFilterItem,
        GroupSearch,
    },

    props: {
        filters: {
            type: Array,
            required: true,
        },
        manuallyAppliedFilters: {
            required: false,
            default: () => [],
            validator: prop => Array.isArray(prop) || prop === null,
        },
        manuallyAppliedGroup: {
            required: false,
            default: () => [],
            validator: prop => Array.isArray(prop) || prop === null,
        },
        tagType: {
            type: String,
            required: false,
            default: '',
        },
        queryBuilder: {
            type: Object,
            default: null,
            validator: prop => typeof prop === 'object' || prop === null,
        },
    },

    emits: ['apply-filter', 'clear-filters', 'filter-ui-dismissed'],

    setup(props, { emit }) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();

        const closeFilterDropdownIfNeeded = filter => {
            if (!shouldCloseFilterDropdownAfterApply(filter)) {
                return;
            }

            // Defer so PrimeVue AutoComplete can finish click handling before unmount.
            nextTick(() => {
                instance?.proxy?.closeDropdown?.();
            });
        };

        // Use the filter manager composable
        const {
            formFields,
            expandedSections,
            filterData,
            appliedFiltersCount,
            availableRoles,
            availableStatuses,
            availableGroups,
            debouncedApplyFilter,
            applyFilterImmediate,
            toggleSection,
            clearFilters,
            addRoleToFilter,
            removeRoleFromFilter,
            getRoleName,
            addStatusToFilter,
            removeStatusFromFilter,
            addGroupToFilter,
            removeGroupFromFilter,
            getGroupName,
            onEnter,
            onAfterEnter,
            onLeave,
        } = useFilterManager(props.filters, emit, { afterApplyFilter: closeFilterDropdownIfNeeded });

        const activeDateFilters = reactive(new Set());

        watch(
            () => props.filters,
            newFilters => {
                const roleFilter = newFilters.find(filterItem => filterItem.type === 'roles');
                if (roleFilter && roleFilter.value && filterData) {
                    filterData.localSelectedRoleIds = roleFilter.value;
                }

                const statusFilter = newFilters.find(filterItem => filterItem.type === 'statuses');
                if (statusFilter && statusFilter.value && filterData) {
                    filterData.localSelectedStatusNames = statusFilter.value;
                }

                newFilters.forEach(filter => {
                    if (filter.type === 'date-range') {
                        const dateValues = formFields[filter.field]?.dates;
                        const hasFormValues = dateValues && dateValues.length > 0 && (dateValues[0] || dateValues[1]);
                        const isApplied = filter.is_applied || hasFormValues;

                        if (isApplied) {
                            activeDateFilters.add(filter.field);
                        } else {
                            activeDateFilters.delete(filter.field);
                        }
                    }
                });
            },
            { immediate: true, deep: true }
        );

        return {
            translate,
            formFields,
            expandedSections,
            filterData,
            appliedFiltersCount,
            availableRoles,
            availableStatuses,
            availableGroups,
            debouncedApplyFilter,
            applyFilterImmediate,
            toggleSection,
            clearFilters,
            addRoleToFilter,
            removeRoleFromFilter,
            getRoleName,
            addStatusToFilter,
            removeStatusFromFilter,
            addGroupToFilter,
            removeGroupFromFilter,
            getGroupName,
            onEnter,
            onAfterEnter,
            onLeave,
            activeDateFilters,
            closeFilterDropdownIfNeeded,
        };
    },

    data() {
        return {
            localManuallyAppliedGroup: this.manuallyAppliedGroup,
            filteredItem: false,
        };
    },

    computed: {
        course() {
            return Course.query().with('course_codes').find(this.$route.params.courseId);
        },
    },

    watch: {
        manuallyAppliedFilters: {
            immediate: true,
            handler(manuallyAppliedFilters) {
                if (manuallyAppliedFilters && manuallyAppliedFilters.length > 0) {
                    manuallyAppliedFilters.forEach(
                        manuallyAppliedFilter => (this.localManuallyAppliedGroup = manuallyAppliedFilter.event)
                    );
                }
            },
        },
    },

    mounted() {
        // Set expanded sections for applied filters and initialize date filter tracking
        this.filters.forEach(filter => {
            if (filter.type === 'date-range') {
                // Check if date filter has values and track it
                const dateValues = this.formFields[filter.field]?.dates;
                const hasFormValues = dateValues && dateValues.length > 0 && (dateValues[0] || dateValues[1]);
                const isApplied = filter.is_applied || hasFormValues;

                if (isApplied) {
                    this.activeDateFilters.add(filter.field);
                    this.expandedSections[filter.field] = true;
                }
            } else if (filter.is_applied || filter.startExpanded) {
                this.expandedSections[filter.field] = true;
            }
        });
    },

    beforeUnmount() {
        // Clean up any event listeners if needed
    },

    methods: {
        handleSectionToggle(filter) {
            // Toggle the section first
            this.toggleSection(filter.field);

            // If we're opening a non-datepicker section, notify datepickers to close
            if (filter.type !== 'date-range') {
                const event = new CustomEvent('filter-section-toggled', {
                    detail: { filterField: filter.field, filterType: filter.type },
                });
                document.dispatchEvent(event);
            }
        },

        keepDropdownOpen() {
            // This method is called when we want to prevent the dropdown from closing
            // For now, it's just a placeholder to handle the event
            // The main logic is in preventing the close, not actively keeping it open
        },

        closeDropdown() {
            const dropdown = this.$refs.filtersDropdown;
            if (dropdown && typeof dropdown.hide === 'function') {
                dropdown.hide();
                this.$emit('filter-ui-dismissed');
                return;
            }

            this.$nextTick(() => {
                let parent = this.$parent;
                while (parent) {
                    if (parent.$options.name === 'BDropdown') {
                        if (typeof parent.toggle === 'function') {
                            parent.toggle();
                            this.$emit('filter-ui-dismissed');
                            return;
                        }
                        break;
                    }
                    parent = parent.$parent;
                }

                const dropdownElement = this.$el.closest('.dropdown');
                if (dropdownElement) {
                    document.body.click();
                    this.$emit('filter-ui-dismissed');
                    return;
                }

                const dropdownTrigger = document.querySelector('.dropdown.is-active .dropdown-trigger button');
                if (dropdownTrigger) {
                    dropdownTrigger.click();
                    this.$emit('filter-ui-dismissed');
                }
            });
        },

        getDateModelValue(filterField) {
            const dateValue = this.formFields[filterField]?.dates;
            return dateValue;
        },

        isAutocompleteFilter(type) {
            const autocompleteTypes = ['users', 'courses', 'course-codes', 'activities', 'tags'];
            return autocompleteTypes.includes(type);
        },

        updateFilterValue(filter, value) {
            if (filter) {
                filter.value = value;
            }
        },

        persistTagSelectedTitle(filter, selectedTitle) {
            if (filter) {
                filter.selectedTitle = selectedTitle;
            }
        },

        handleGroupFilter(event, filter) {
            this.filteredItem = true;
            // Persist the selection so the input repopulates and the badge counts it
            // after the dropdown closes (PrimeVue unmounts the panel content on hide).
            this.localManuallyAppliedGroup = event || null;
            this.updateFilterValue(filter, event ? event.id : null);
            this.debouncedApplyFilter(filter, event ? event.id : null, event);
        },

        handleEitherOrFilter(filter, value) {
            this.filteredItem = true;
            this.$emit('apply-filter', filter.captureEvent({ value }));
            this.closeFilterDropdownIfNeeded(filter);
        },

        handleMinMaxFilter(filter, value, event) {
            const { min, max } = value;
            this.filteredItem = true;
            // Persist so inputs repopulate and the badge counts after the panel unmounts.
            this.formFields[filter.field] = { min, max };
            this.$emit('apply-filter', filter.captureEvent({ event, min, max }));
            this.closeFilterDropdownIfNeeded(filter);
        },

        handleDateRangeFilter(filter, dates) {
            this.$nextTick(() => {
                this.filteredItem = true;
                // Update form fields to track the dates
                this.formFields[filter.field] = { dates };

                // Keep activeDateFilters in sync for backward compatibility
                const hasValidDates = dates && Array.isArray(dates) && dates.length > 0 && (dates[0] || dates[1]);

                if (hasValidDates) {
                    this.activeDateFilters.add(filter.field);
                } else {
                    this.activeDateFilters.delete(filter.field);
                }

                this.$emit('apply-filter', filter.captureEvent({ dates }));
            });
        },

        handleDropdownFilter(filter, value) {
            this.filteredItem = true;
            this.$emit('apply-filter', filter.captureEvent({ value }));
            this.closeFilterDropdownIfNeeded(filter);
        },

        // Check if a filter is applied (handles date filters specially)
        isFilterApplied(filter) {
            // Read refs so Vue tracks modal filters (Course/ActivityType) when QueryBuilder was reset
            Boolean(filter.value);
            Boolean(filter.selectedTitle);

            if (filter.type === 'date-range') {
                // For date filters, check multiple sources:
                // 1. The filter's is_applied property
                // 2. Form field values (dates array)
                // 3. Active date filters set (for backward compatibility with tests)
                const hasFilterValue = Boolean(filter.is_applied);
                const formFieldValue = this.formFields[filter.field];
                const hasFormValue =
                    formFieldValue?.dates &&
                    Array.isArray(formFieldValue.dates) &&
                    formFieldValue.dates.length > 0 &&
                    (formFieldValue.dates[0] || formFieldValue.dates[1]);
                const isInActiveDateFilters = this.activeDateFilters.has(filter.field);

                return hasFilterValue || Boolean(hasFormValue) || isInActiveDateFilters;
            }

            // Text/quiz reflect the live input instantly (is_applied is debounced + non-reactive).
            if (filter.type === 'text' || filter.type === 'quiz') {
                const fieldValue = this.formFields[filter.field];
                if (fieldValue === '') {
                    return false;
                }

                return Boolean(filter.is_applied) || Boolean(fieldValue);
            }

            // Min/max reflects the live inputs instantly, same as the count badge.
            if (filter.type === 'min-max') {
                const fieldValue = this.formFields[filter.field];

                return Boolean(fieldValue?.min) || Boolean(fieldValue?.max);
            }

            return Boolean(filter.is_applied);
        },

        // Clear filters and reset date filter tracking
        clearAllFilters() {
            this.activeDateFilters.clear();
            this.localManuallyAppliedGroup = null;
            this.clearFilters();
            this.closeDropdown();
        },
    },
};
</script>

<style lang="scss" scoped>
@import './FilterDropdown/FilterDropdown.scss';
</style>
